/**
 * A post card's "Saved by N" after the viewer saves or unsaves it from the
 * card, without a reload (docs/plans/POST_SAVE_ATTRIBUTION.md).
 *
 * N counts other people only, each once, and only while they have it saved
 * (postSaveCountsService), so the number moves only when the viewer's own
 * standing in it changes. The server says where they stand:
 * `viewer_in_save_count` (they are one of the N), `viewer_saved_from_inside`
 * (they are, through a place they saved from inside the post — unsaving the
 * post itself leaves that standing) and `viewer_save_counts` (a save of theirs
 * would be — not the creator, nor one of the trip's own people). Without
 * those (an older response, a non-post card) only `user_saved` changes.
 *
 * @param {Object} item the card as it was
 * @param {boolean} isSaved whether the viewer has it saved now
 * @returns {Object} the card with user_saved and, when it moved, save_count
 */
export function withSaveCount(item, isSaved) {
  const next = { ...item, user_saved: isSaved };
  // A profile card carries no user_saved: being in the count is then the
  // best "was saved" there is, or its first unsave would move nothing.
  const wasSaved = item?.user_saved == null ? !!item?.viewer_in_save_count : !!item.user_saved;
  if (typeof item?.save_count !== 'number' || wasSaved === isSaved) return next;
  if (isSaved && item.viewer_save_counts && !item.viewer_in_save_count) {
    return { ...next, save_count: item.save_count + 1, viewer_in_save_count: true };
  }
  if (!isSaved && item.viewer_in_save_count && !item.viewer_saved_from_inside) {
    return { ...next, save_count: Math.max(0, item.save_count - 1), viewer_in_save_count: false };
  }
  return next;
}

/** The post types a card shows "Saved by N" for (ActionRow). */
export const POST_SAVE_COUNT_TYPES = new Set(['trip', 'album', 'activity', 'collection', 'board']);

/**
 * The card once the server has answered: its count and the viewer's standing,
 * as GET /api/saves/standing returns them. The ±1 above is a guess from what
 * the save sheet shows; the sheet can't see every save that counts (a place
 * already kept on a board or in a trip collection, a post saved from its own
 * page), so this is where the number settles.
 */
export function withSaveStanding(item, standing) {
  if (!standing || typeof standing.save_count !== 'number') return item;
  return {
    ...item,
    save_count: standing.save_count,
    viewer_in_save_count: !!standing.viewer_in_save_count,
    viewer_saved_from_inside: !!standing.viewer_saved_from_inside,
    viewer_save_counts: !!standing.viewer_save_counts
  };
}
